import { ReactNode } from 'react';
import { useMediaQuery } from '@mui/material';
import l from '../../../../lang';
import ButtonLink from '../../../common/button-link';
import { Chip } from '../../../common/chip';
import InputSearch from '../../../common/input-search';
import Select from '../../../common/select';
import { Filters } from '../../../../store/interfaces';
import { useAvailableCountries } from '../../../../hooks/use-available-countries';
import ClientsFilterGrouped from './filter-grouped';
import './styles.scss';

interface ClientsFilterProps {
  loading: boolean;
  appliedFilters: Filters;
  clearFilters: () => void;
  changeFilters: (filters: Filters) => void;
  exportButton?: ReactNode;
}

const ClientsFilter = ({ loading, appliedFilters, clearFilters, changeFilters, exportButton }: ClientsFilterProps) => {
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const allCountries = useAvailableCountries();

  const sortDirectionOptions = {
    desc: l('clients.filter.sortDirection.desc'),
    asc: l('clients.filter.sortDirection.asc'),
  };

  const sortByOptions = {
    client_id: l('clients.filter.sortBy.merchantClientId'),
    transacted_amount: l('clients.transactedAmount'),
    first_name: l('clients.filter.sortBy.firstName'),
    last_name: l('clients.filter.sortBy.lastName'),
    email: l('clients.filter.sortBy.email'),
    created_date: l('clients.filter.sortBy.createdAt'),
  };

  const statusOptions = {
    ACTIVE: l('clients.status.ACTIVE'),
    INACTIVE: l('clients.status.INACTIVE'),
    NEW: l('clients.status.NEW'),
  };

  const countryOptions = allCountries.reduce<Record<string, string>>((acc, countryCode) => {
    acc[countryCode] = l(`countryCode.${countryCode}`);
    return acc;
  }, {});

  const chipText = (keyFilter: string, value: string): { label: string; value: string } => {
    switch (keyFilter) {
      case 'country':
        return { label: l(`countryCode.${value}`), value };
      case 'status':
        return { label: statusOptions[value as keyof typeof statusOptions] || value, value };
      case 'sort_direction':
        return { label: sortDirectionOptions[value as keyof typeof sortDirectionOptions] || value, value };
      case 'sort_by':
        return { label: sortByOptions[value as keyof typeof sortByOptions] || value, value };
      default:
        return { label: value, value };
    }
  };

  return (
    <div className="clients_filter">
      <div className="clients_filter__search">
        <InputSearch
          value={appliedFilters.search}
          showSearchType={false}
          placeholder={l('clients.filter.search.placeholder')}
          isLoading={loading}
          helpText={l('clients.filter.search.placeholder')}
          onSearch={(_, value) => {
            changeFilters({ ...appliedFilters, search: value });
          }}
        />
      </div>
      {isDesktop ? (
        <>
          <div className="clients_filter__select">
            <Select
              name="sort_direction"
              defaultOptionText={l('clients.filter.sortDirection')}
              defaultOptionEnabled
              value={appliedFilters.sort_direction}
              disabled={loading}
              size="sm"
              options={sortDirectionOptions}
              onChange={e => {
                changeFilters({ ...appliedFilters, sort_direction: e.target.value });
              }}
            />
          </div>
          <div className="clients_filter__select">
            <Select
              name="sort_by"
              defaultOptionText={l('clients.filter.sortBy')}
              defaultOptionEnabled
              value={appliedFilters.sort_by}
              disabled={loading}
              size="sm"
              options={sortByOptions}
              onChange={e => {
                changeFilters({ ...appliedFilters, sort_by: e.target.value });
              }}
            />
          </div>
          <div className="clients_filter__select">
            <Select
              name="country"
              defaultOptionText={l('clients.filter.country')}
              defaultOptionEnabled
              value={appliedFilters.country}
              disabled={loading}
              size="sm"
              options={countryOptions}
              onChange={e => {
                changeFilters({ ...appliedFilters, country: e.target.value });
              }}
            />
          </div>
          <div className="clients_filter__select">
            <Select
              name="status"
              defaultOptionText={l('clients.filter.status')}
              defaultOptionEnabled
              value={appliedFilters.status}
              disabled={loading}
              size="sm"
              options={statusOptions}
              onChange={e => {
                changeFilters({ ...appliedFilters, status: e.target.value });
              }}
            />
          </div>
          {exportButton && <div className="clients_filter__export">{exportButton}</div>}
        </>
      ) : (
        <div className="clients_filter__button_filter_wrapper">
          <ClientsFilterGrouped
            appliedFilters={appliedFilters}
            clearFilters={clearFilters}
            changeFilters={changeFilters}
            exportButton={exportButton}
          />
        </div>
      )}
      {Object.keys(appliedFilters).some(keyFilter => appliedFilters[keyFilter] !== '') && (
        <>
          <div className="clients_filter__divider" />
          <div className="clients_filter__applied_filters_wrapper">
            <div className="clients_filter__applied_filters">
              <span>{l('transactions.filter.appliedFilters')}: </span>
              {!isDesktop && (
                <>
                  <span className="clients_filter__clear_filters_mobile">
                    <ButtonLink text={l('transactions.filter.clearFilters')} onClick={clearFilters} />
                  </span>
                  <br />
                </>
              )}
              {Object.keys(appliedFilters).map(keyFilter => {
                if (!appliedFilters[keyFilter]) {
                  return null;
                }
                if (keyFilter === 'sort_order' || keyFilter === 'sort_by' || keyFilter === 'sort_direction') {
                  return null;
                }
                const chip = chipText(keyFilter, appliedFilters[keyFilter]);
                return (
                  <div key={`${keyFilter}-${chip.value}`} className="clients_filter__chip_wrapper">
                    <Chip
                      label={chip.label}
                      onDelete={() =>
                        changeFilters({
                          ...appliedFilters,
                          [keyFilter]: '',
                        })
                      }
                    />
                  </div>
                );
              })}
            </div>
            <div className="clients_filter__clear_filters_desktop">
              <ButtonLink text={l('transactions.filter.clearFilters')} onClick={clearFilters} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientsFilter;
